import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Play,
  Star,
  Users,
  Zap,
  Wallet,
  TrendingUp,
  ArrowRight,
  Check,
  CreditCard,
  Menu,
  X,
  Gamepad,
  Shield,
  Settings,
  IndianRupee,
  Sliders,
  Target,
  Sparkles,
  LogOut,
  User,
  Phone,
  Mail,
  Youtube,
  Instagram,
  MapPin,
  X as CloseIcon
} from "lucide-react";
import { getFromLocalStorage, removeFromLocalStorage } from "./utils/localStorage";
import axios from "axios";
import logo from './assets/Images/logo.png';
import { toast } from 'react-toastify';

const HomePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [broadcastCards, setBroadcastCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is logged in
  const token = getFromLocalStorage("token");
  const isLoggedIn = (token && token.trim() !== "");

  // Derive role from local storage and membership plan (consistent with Header/Layout)
  const computeRoleFromLocalData = () => {
    try {
      const storedRole = localStorage.getItem('userRole');
      const userRaw = localStorage.getItem('user');
      let derived = null;
      if (userRaw) {
        const u = typeof userRaw === 'string' ? JSON.parse(userRaw) : userRaw;
        const userType = u?.user_type || u?.role || 'user';
        const membershipPlan = u?.membershipPlan || 'free';
        if (userType === 'creator' && membershipPlan === 'pro') derived = 'creator_pro';
        else if (userType === 'creator') derived = 'creator';
        else derived = 'user';
      }
      const finalRole = storedRole || derived || 'user';
      if (finalRole && finalRole !== storedRole) localStorage.setItem('userRole', finalRole);
      return finalRole;
    } catch {
      return localStorage.getItem('userRole') || 'user';
    }
  };

  const userRole = computeRoleFromLocalData();
  const isCreatorOrPro = userRole === 'creator' || userRole === 'creator_pro';

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  // Fetch daily broadcast cards from backend + auto-refresh
  useEffect(() => {
    let isMounted = true;

    const fetchBroadcastCards = async (background = false) => {
      try {
        if (!background) setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/today`);
        if (!isMounted) return;

        if (response.data?.success && Array.isArray(response.data.cards)) {
          setBroadcastCards(response.data.cards.slice(0, 3));
        } else {
          setBroadcastCards([
            { _id: '1', title: 'BGMI Squad Challenge', description: 'Team-based competitive challenges', imageUrl: '/api/placeholder/400/600' },
            { _id: '2', title: 'Solo Victory Challenge', description: 'Test your individual skills', imageUrl: '/api/placeholder/400/600' },
            { _id: '3', title: 'Trickshot Contest', description: 'Show off your best trickshots', imageUrl: '/api/placeholder/400/600' }
          ]);
        }
      } catch (error) {
        console.error('Error fetching broadcast cards:', error);
      } finally {
        if (isMounted && !background) setLoading(false);
      }
    };

    // First load: show spinner
    fetchBroadcastCards(false);

    // Auto-refresh hooks: silent/background refreshes (no spinner)
    const onStorage = (e) => {
      if (e.key === 'crezar_cards_updated') fetchBroadcastCards(true);
    };
    const onCustom = () => fetchBroadcastCards(true);
    const onFocus = () => fetchBroadcastCards(true);

    window.addEventListener('storage', onStorage);
    window.addEventListener('crezar:cardsUpdated', onCustom);
    window.addEventListener('focus', onFocus);
    const interval = setInterval(() => fetchBroadcastCards(true), 60000);

    return () => {
      isMounted = false;
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('crezar:cardsUpdated', onCustom);
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  }, [API_BASE_URL]);

  // Removed redundant challenge cards fetch effect that referenced undefined setChallengeCards

  // Active card index (center focus), modal state, and countdown
  const [activeIndex, setActiveIndex] = useState(1);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [modalCard, setModalCard] = useState(null);
  const [countdown, setCountdown] = useState('');

  // Creator Pro lead form modal state
  const [showProForm, setShowProForm] = useState(false);
  const [proFormSubmitting, setProFormSubmitting] = useState(false);
  const [proFormSubmitted, setProFormSubmitted] = useState(false);
  const [proFormData, setProFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    youtube: '',
    instagram: '',
    location: ''
  });

  // Creator (Free) lead form modal state
  const [showCreatorForm, setShowCreatorForm] = useState(false);
  const [creatorFormSubmitting, setCreatorFormSubmitting] = useState(false);
  const [creatorFormSubmitted, setCreatorFormSubmitted] = useState(false);
  const [creatorFormData, setCreatorFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    youtube: '',
    instagram: '',
    location: ''
  });

  // Keep activeIndex within available cards
  useEffect(() => {
    if (broadcastCards.length > 0) {
      setActiveIndex(Math.min(1, broadcastCards.length - 1));
    }
  }, [broadcastCards.length]);

  // Compute countdown to scheduledDate + scheduleTime for active card
  useEffect(() => {
    const buildDateTime = (card) => {
      try {
        const dateStr = (card?.scheduledDate || new Date().toISOString().split('T')[0]).slice(0, 10);
        const timeStr = (card?.scheduleTime || '15:00').slice(0, 5);
        const iso = `${dateStr}T${timeStr}:00`;
        return new Date(iso);
      } catch {
        return new Date();
      }
    };
    const formatDiff = (ms) => {
      if (ms <= 0) return 'Live now';
      const s = Math.floor(ms / 1000);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const ss = s % 60;
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(h)}:${pad(m)}:${pad(ss)}`;
    };
    const tick = () => {
      const topIndex = hoverIndex ?? activeIndex;
      const c = broadcastCards[topIndex];
      if (!c) return setCountdown('');
      const target = buildDateTime(c);
      setCountdown(formatDiff(target.getTime() - Date.now()));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [broadcastCards, activeIndex, hoverIndex]);

  // Validation helper
  const validateForm = (data) => {
    const { phone, email, youtube, instagram } = data;

    // Phone: 10 digits
    const phoneRegex = /^\d{10}$/;
    if (phone && !phoneRegex.test(phone)) {
      toast.error('Phone number must be exactly 10 digits');
      return false;
    }

    // Email: basic regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // YouTube: URL validation
    if (youtube && !youtube.startsWith('http')) {
      toast.error('YouTube link must start with http:// or https://');
      return false;
    }

    // Instagram: URL validation
    if (instagram && !instagram.startsWith('http')) {
      toast.error('Instagram link must start with http:// or https://');
      return false;
    }

    return true;
  };

  // Creator Pro Lead Form helpers
  const openProForm = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setShowProForm(true);
  };
  const closeProForm = () => {
    setShowProForm(false);
    setProFormSubmitted(false);
  };
  const handleProFormChange = (e) => {
    const { name, value } = e.target;
    setProFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleProFormSubmit = async (e) => {
    e.preventDefault();
    try {
      setProFormSubmitting(true);
      const { firstName, lastName, phone, email, youtube, instagram, location } = proFormData;
      if (!firstName || !lastName || !phone || !email) {
        toast.error('Please fill all required fields');
        return;
      }
      if (!validateForm(proFormData)) return;
      const payload = {
        name: `${firstName} ${lastName}`.trim(),
        email,
        subject: 'Creator Pro Application',
        message:
          `Creator Pro Application\n` +
          `First Name: ${firstName}\n` +
          `Last Name: ${lastName}\n` +
          `Contact Number: ${phone}\n` +
          `Email: ${email}\n` +
          `YouTube: ${youtube || '-'}\n` +
          `Instagram: ${instagram || '-'}\n` +
          `Location: ${location || '-'}\n` +
          `Source: Homepage Pricing (Beta access request)`,
        planType: 'creator_pro',
        source: 'Homepage Pricing (Beta access request)',
        meta: { phone, youtube, instagram, location }
      };
      await axios.post(`${API_BASE_URL}/auth/user/contactus`, payload);
      setProFormSubmitted(true);
      toast.success('Application submitted');
    } catch (err) {
      console.error('Creator Pro form submit error:', err);
      toast.error(err?.response?.data?.error || 'Failed to submit application');
    } finally {
      setProFormSubmitting(false);
    }
  };

  // Creator (Free) Lead Form helpers
  const openCreatorForm = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setShowCreatorForm(true);
  };
  const closeCreatorForm = () => {
    setShowCreatorForm(false);
    setCreatorFormSubmitted(false);
  };
  const handleCreatorFormChange = (e) => {
    const { name, value } = e.target;
    setCreatorFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleCreatorFormSubmit = async (e) => {
    e.preventDefault();
    try {
      setCreatorFormSubmitting(true);
      const { firstName, lastName, phone, email, youtube, instagram, location } = creatorFormData;
      if (!firstName || !lastName || !phone || !email || !youtube || !instagram) {
        toast.error('Please fill all required fields');
        return;
      }
      if (!validateForm(creatorFormData)) return;
      const payload = {
        name: `${firstName} ${lastName}`.trim(),
        email,
        subject: 'Creator Application (Free)',
        message:
          `Creator (Free) Application\n` +
          `First Name: ${firstName}\n` +
          `Last Name: ${lastName}\n` +
          `Contact Number: ${phone}\n` +
          `Email: ${email}\n` +
          `YouTube: ${youtube || '-'}\n` +
          `Instagram: ${instagram || '-'}\n` +
          `Location: ${location || '-'}\n` +
          `Source: Homepage Pricing (Free trial request)`,
        planType: 'creator',
        source: 'Homepage Pricing (Free trial request)',
        meta: { phone, youtube, instagram, location }
      };
      await axios.post(`${API_BASE_URL}/auth/user/contactus`, payload);
      setCreatorFormSubmitted(true);
      toast.success('Application submitted');
    } catch (err) {
      console.error('Creator (Free) form submit error:', err);
      toast.error(err?.response?.data?.error || 'Failed to submit application');
    } finally {
      setCreatorFormSubmitting(false);
    }
  };

  const handleLogout = () => {
    removeFromLocalStorage("token");
    removeFromLocalStorage("adminToken");
    removeFromLocalStorage("user");
    navigate("/");
    window.location.reload();
  };

  // Only smooth-scroll when explicitly requested via navigation state
  useEffect(() => {
    if (location.hash && location.state && location.state.scroll === true) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location]);

  // Helper to handle in-page nav from header/menu
  const handleAnchorNav = (hash) => (e) => {
    e.preventDefault();
    if (window.location.pathname === "/") {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate({ pathname: "/", hash }, { state: { scroll: true } });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white overflow-x-hidden">
      {/* Futuristic Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-conic from-yellow-500/10 via-transparent to-yellow-500/10 rounded-full blur-3xl animate-spin" style={{ animationDuration: '20s' }}></div>
      </div>

      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-xl border-b border-yellow-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src={logo}
                alt="Crezar Logo"
                className="w-12 h-12 md:w-16 md:h-16 object-contain rounded-xl"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">
                CREZAR
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                Home
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              {isLoggedIn ? (
                <>
                  <Link to="/dashboard" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                    Dashboard
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                  <Link to="/streamers" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                    Streamers
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                  <Link to="/my-cards" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                    My Cards
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                </>
              ) : (
                <>
                  <a href="#features" onClick={handleAnchorNav('#features')} className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                    Features
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                  </a>
                  <a href="#pricing" onClick={handleAnchorNav('#pricing')} className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                    Pricing
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                  </a>
                  {/* TODO: Uncomment when demo is ready
                  <a href="#demo" onClick={handleAnchorNav('#demo')} className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                    Demo
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                  </a>
                  */}
                </>
              )}
              {isLoggedIn && isCreatorOrPro && (
                <Link to="/overlay" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                  Overlay
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
                </Link>
              )}
              <Link to="/challenges" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                Challenges
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/contactus" className="text-white hover:text-yellow-400 transition-all duration-300 relative group">
                Contact
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              {isLoggedIn ? (
                <>
                  <Link to="/profile" className="text-white hover:text-yellow-400 transition-colors flex items-center space-x-1">
                    <User className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-white hover:text-yellow-400 transition-colors flex items-center space-x-1"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-white hover:text-yellow-400 transition-colors"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/signup"
                    className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-6 py-2 rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 shadow-lg shadow-yellow-500/25 pointer-events-auto z-50"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white hover:text-yellow-400 transition-colors"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-lg border-t border-yellow-500/20">
            <div className="px-4 py-6 space-y-4">
              <Link to="/" className="block text-white hover:text-yellow-400 transition-colors">Home</Link>
              {isLoggedIn ? (
                <>
                  <Link to="/dashboard" className="block text-white hover:text-yellow-400 transition-colors">Dashboard</Link>
                  <Link to="/streamers" className="block text-white hover:text-yellow-400 transition-colors">Streamers</Link>
                  <Link to="/my-cards" className="block text-white hover:text-yellow-400 transition-colors">My Cards</Link>
                </>
              ) : (
                <>
                  <a href="#features" onClick={handleAnchorNav('#features')} className="block text-white hover:text-yellow-400 transition-colors">Features</a>
                  <a href="#pricing" onClick={handleAnchorNav('#pricing')} className="block text-white hover:text-yellow-400 transition-colors">Pricing</a>
                  {/* TODO: Uncomment when demo is ready
                  <a href="#demo" onClick={handleAnchorNav('#demo')} className="block text-white hover:text-yellow-400 transition-colors">Demo</a>
                  */}
                </>
              )}
              {isLoggedIn && isCreatorOrPro && (
                <Link to="/overlay" className="block text-white hover:text-yellow-400 transition-colors">Overlay</Link>
              )}
              <Link to="/challenges" className="block text-white hover:text-yellow-400 transition-colors">Challenges</Link>
              <Link to="/contactus" className="block text-white hover:text-yellow-400 transition-colors">Contact</Link>
              <div className="pt-4 space-y-2">
                {isLoggedIn ? (
                  <>
                    <Link to="/profile" className="block text-white hover:text-yellow-400 transition-colors">Profile</Link>
                    <button onClick={handleLogout} className="block text-white hover:text-yellow-400 transition-colors">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="block text-white hover:text-yellow-400 transition-colors">Log in</Link>
                    <Link
                      to="/signup"
                      className="block bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-6 py-2 rounded-lg font-semibold text-center pointer-events-auto z-50"
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 relative z-10">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 rounded-full px-4 py-2 backdrop-blur-sm">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <span className="text-sm text-yellow-400 font-medium">Next-Gen Streaming Platform</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">
                <div>Transform Your</div>
                <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent animate-pulse">
                  Live Stream with
                </div>
                <div>Interactive </div>
                <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent animate-pulse pb-0.5 leading-tight">
                  Challenges
                </div>
              </h1>

              <p className="text-xl text-gray-300 leading-relaxed">
                Push Your Limits. Head On the Challenges. Maximize Your Engagement.
                For gamers and IRL creators — connect deeper, engage smarter, and grow faster.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="button"
                  onClick={openCreatorForm}
                  className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 shadow-lg shadow-yellow-500/25 pointer-events-auto z-50"
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
                {/* TODO: Uncomment when demo is ready
                <button className="border border-yellow-500/30 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-yellow-500/10 transition-all flex items-center justify-center backdrop-blur-sm">
                  <Play className="w-5 h-5 mr-2" />
                  Watch Demo
                </button>
                */}
              </div>

              <div className="grid grid-cols-3 gap-8 pt-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-400">10+</div>
                  <div className="text-sm text-gray-400">Active Streamers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-400">100+</div>
                  <div className="text-sm text-gray-400">Cards Created</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-400">500%</div>
                  <div className="text-sm text-gray-400">Engament Boosted</div>
                </div>
              </div>
            </div>

            <div className="relative z-10 perspective-1000">
              {/* 3D Card Stack */}
              <div className="relative flex items-center justify-center h-[28rem]">
                {loading ? (
                  <div className="flex space-x-4">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <div
                        key={index}
                        className="w-64 h-96 bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl border border-yellow-500/20 animate-pulse"
                        style={{
                          transform: `translateX(${(index - 1) * 60}px) rotateY(${(index - 1) * 15}deg)`,
                          zIndex: index === 1 ? 30 : 20 - Math.abs(index - 1) * 5
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="relative flex items-center justify-center">
                    {(() => {
                      const topIndex = hoverIndex ?? activeIndex;
                      return broadcastCards.slice(0, 3).map((card, index) => {
                        const hasImage = !!card.imageUrl;
                        const imgSrc = hasImage
                          ? (card.imageUrl.startsWith('http') ? card.imageUrl : `${API_BASE_URL}${card.imageUrl}`)
                          : `${API_BASE_URL}/api/placeholder/400/600`;
                        // Fixed positions: left(0), center(1), right(2)
                        const translateX = (index - 1) * 90;
                        const rotateY = (index - 1) * 12;
                        const z = index === topIndex ? 50 : (index === 1 ? 30 : 20 - Math.abs(index - 1) * 5);

                        return (
                          <div
                            key={card._id || index}
                            onMouseEnter={() => setHoverIndex(index)}
                            onMouseLeave={() => setHoverIndex(null)}
                            onClick={() => (index === (hoverIndex ?? activeIndex) ? setModalCard(card) : setActiveIndex(index))}
                            className={"absolute w-64 h-96 group cursor-pointer"}
                            style={{ transform: `translateX(${translateX}px) rotateY(${rotateY}deg)`, zIndex: z }}
                          >
                            {/* Neon halo behind active card (non-intrusive, never covers content) */}
                            {index === topIndex && (
                              <div
                                className="absolute -inset-4 rounded-[2rem] pointer-events-none"
                                style={{
                                  background: 'radial-gradient(60% 60% at 50% 50%, rgba(255,221,94,0.18) 0%, rgba(255,221,94,0) 65%)',
                                  filter: 'blur(18px) saturate(115%)',
                                  boxShadow: '0 0 40px rgba(234,179,8,0.55), 0 0 88px rgba(234,179,8,0.35)'
                                }}
                              />
                            )}
                            {/* Inner wrapper handles hover scaling while position stays fixed */}
                            <div className={`relative w-full h-full rounded-3xl overflow-hidden transform-gpu transition-transform duration-300 ${(index === topIndex) ? 'scale-[1.08]' : 'scale-100'} group-hover:scale-[1.18]`}
                            >
                              {/* Card Frame with subtle glow */}
                              <div
                                className={`absolute inset-0 rounded-3xl overflow-hidden bg-gradient-to-br from-gray-900 via-black to-gray-900 border-4 ${(index === topIndex) ? 'border-yellow-400/60' : 'border-yellow-400/25'} shadow-[0_0_14px_rgba(234,179,8,0.2)] group-hover:shadow-[0_0_36px_rgba(234,179,8,0.45)] transition-shadow`}
                              >
                                {/* Image */}
                                <div className="absolute inset-0">
                                  <img
                                    src={imgSrc}
                                    alt={card.title || `Challenge Card ${index + 1}`}
                                    className="w-full h-full object-cover"
                                    loading={index === activeIndex ? 'eager' : 'lazy'}
                                  />
                                </div>

                                {/* Subtle hover border glow */}
                                <div className={`absolute inset-0 ring-1 rounded-3xl ${(index === topIndex) ? 'ring-yellow-400/60' : 'ring-yellow-400/20'} group-hover:ring-yellow-300/70 transition`}></div>
                              </div>
                            </div>

                            {/* Timer under active card only */}
                            {index === topIndex && (
                              <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
                                <div className="relative">
                                  <div className="px-5 py-2.5 rounded-full bg-black/70 backdrop-blur-sm border border-yellow-400/50 shadow-[0_0_26px_rgba(234,179,8,0.35)] text-center">
                                    <div className="text-[12px] uppercase tracking-widest text-yellow-300/90">Starts in</div>
                                    <div className="text-yellow-300 font-extrabold text-lg tabular-nums tracking-wider">{countdown}</div>
                                  </div>
                                  <div className="absolute -inset-1 rounded-full bg-yellow-400/10 blur-xl -z-10"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Card Modal */}
      {modalCard && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModalCard(null)}>
          <div className="relative max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute -top-10 right-0 text-white hover:text-yellow-400 transition-colors"
              onClick={() => setModalCard(null)}
            >
              <CloseIcon className="w-8 h-8" />
            </button>
            <div className="relative aspect-[3/4] w-full rounded-2xl overflow-hidden shadow-2xl border border-yellow-500/30">
              <img
                src={modalCard.imageUrl?.startsWith('http') ? modalCard.imageUrl : `${API_BASE_URL}${modalCard.imageUrl}`}
                alt={modalCard.title}
                className="w-full h-full object-cover"
              />
              {/* Optional: Add a subtle gradient overlay at the bottom if needed, but keeping it clean for now */}
              <div className="absolute inset-0 ring-1 ring-yellow-400/20 rounded-2xl pointer-events-none"></div>
            </div>
          </div>
        </div>
      )}

      {/* Creator (Free) Application Modal */}
      {showCreatorForm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl bg-gray-900/90 border border-yellow-500/30 rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-yellow-500/20 bg-gradient-to-r from-yellow-500/10 to-transparent">
              <h4 className="text-xl font-semibold text-yellow-400">Apply for Creator (Free)</h4>
              <button onClick={closeCreatorForm} className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-white/5" aria-label="Close">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreatorFormSubmit} className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">First Name</label>
                  <div className="relative">
                    <input name="firstName" value={creatorFormData.firstName} onChange={handleCreatorFormChange} required className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="John" />
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Last Name</label>
                  <div className="relative">
                    <input name="lastName" value={creatorFormData.lastName} onChange={handleCreatorFormChange} required className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="Doe" />
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Contact Number</label>
                  <div className="relative">
                    <input name="phone" value={creatorFormData.phone} onChange={handleCreatorFormChange} required className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="+91 98765 43210" />
                    <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Email Address</label>
                  <div className="relative">
                    <input type="email" name="email" value={creatorFormData.email} onChange={handleCreatorFormChange} required className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="you@example.com" />
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">YouTube Channel Link</label>
                  <div className="relative">
                    <input name="youtube" value={creatorFormData.youtube} onChange={handleCreatorFormChange} required className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="https://youtube.com/@yourchannel" />
                    <Youtube className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Instagram Account</label>
                  <div className="relative">
                    <input name="instagram" value={creatorFormData.instagram} onChange={handleCreatorFormChange} required className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="https://instagram.com/yourhandle" />
                    <Instagram className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Location</label>
                <div className="relative">
                  <input name="location" value={creatorFormData.location} onChange={handleCreatorFormChange} className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="City, Country" />
                  <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>
              {creatorFormSubmitted && (
                <p className="text-green-400 text-sm">Thank you! Our team will reach out to you within 48 Hours.</p>
              )}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={closeCreatorForm} className="px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-white/5">Cancel</button>
                <button type="submit" disabled={creatorFormSubmitting} className={`px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold hover:from-yellow-500 hover:to-yellow-700 ${creatorFormSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  {creatorFormSubmitting ? 'Submitting…' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Features Section */}
      <section id="features" className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-gray-900/50 to-transparent relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Why Choose{" "}
              <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                Crezar?
              </span>
            </h2>
            <p className="text-xl text-gray-300">A platform designed to connect creators with their audience like never before.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Gamepad className="w-8 h-8" />,
                title: "Challenge Cards",
                description: "Designed to make every live stream interactive and memorable"
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: "Boost Engagement 500%",
                description: "Interactive challenges designed to increase audience participation and fun"
              },
              {
                icon: <TrendingUp className="w-8 h-8" />,
                title: "Smart Revenue Growth",
                description: "Turn every fan interaction into monetization"
              },
              {
                icon: <Zap className="w-8 h-8" />,
                title: "Live Stream Integration",
                description: "Deliver interactive cards instantly to your live streams via OBS or any other streaming platform  "
              },
              {
                icon: <Wallet className="w-8 h-8" />,
                title: "Reliable Payouts",
                description: "Receive your earnings every 15 days, securely and hassle-free, while you focus on streaming"
              },
              {
                icon: <CreditCard className="w-8 h-8" />,
                title: "Customize Challenges",
                description: "Unlock all features with a simple subscription and create challenges your way"
              }
            ].map((feature, index) => (
              <div key={index} className="bg-black/30 rounded-xl p-6 border border-gray-800 hover:border-yellow-500/50 transition-all group backdrop-blur-sm hover:bg-black/40">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-yellow-500/25">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Upgrade to {" "}
              <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                Creator Pro
              </span>
            </h2>
            <p className="text-xl text-gray-300">Make Your Streams Stand Out</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                name: "Creator",
                price: "Free",
                period: "",
                description: "Basic features to get started",
                features: [
                  "Upto 3 Daily Broadcast Challenges",
                  "Simple OBS Integration",
                  "No Customisation",
                  "No Monetization"
                ],
                cta: "Get Started",
                popular: false
              },
              {
                name: "Creator Pro",
                price: "₹349",
                originalPrice: "₹999",
                period: "/month",
                description: "For creators who want to stand out",
                features: [
                  "3 Daily Broadcast Challenges",
                  "Simple OBS Integration",
                  "Challenge Customization",
                  "Monetize Your Streams with Custom Cards",
                  "Priority Support",
                ],
                cta: "Go Pro",
                popular: true
              }
            ].map((plan, index) => (
              <div key={index} className={`relative bg-black/30 rounded-xl p-8 border transition-all backdrop-blur-sm ${plan.popular
                ? 'border-yellow-500 shadow-lg shadow-yellow-500/20 scale-105'
                : 'border-gray-800 hover:border-yellow-500/50'
                }`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span onClick={openProForm} className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-4 py-1 rounded-full text-sm font-semibold shadow-lg cursor-pointer select-none">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'cursor-pointer' : ''}`} onClick={plan.popular ? openProForm : undefined}>{plan.name}</h3>
                  <div className="mb-2">
                    {plan.originalPrice ? (
                      <div className="flex items-center justify-center">
                        <span className="text-lg text-gray-400 line-through mr-2">{plan.originalPrice}</span>
                        <span className="text-4xl font-bold text-yellow-400">{plan.price}</span>
                        {plan.period && <span className="text-gray-400">{plan.period}</span>}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <span className="text-4xl font-bold">{plan.price}</span>
                        {plan.period && <span className="text-gray-400">{plan.period}</span>}
                      </div>
                    )}
                  </div>
                  <p className="text-gray-400">{plan.description}</p>
                  {plan.originalPrice && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-green-500/20 to-green-600/20 border border-green-500/30 rounded-full text-sm text-green-400 font-medium">
                        <Sparkles className="w-3 h-3 mr-1" />
                        65% OFF Launch Offer
                      </span>
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => {
                    const isNegative = plan.name === 'Creator' && featureIndex >= 2;
                    return (
                      <li key={featureIndex} className="flex items-center">
                        {isNegative ? (
                          <X className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" />
                        ) : (
                          <Check className="w-5 h-5 text-yellow-400 mr-3 flex-shrink-0" />
                        )}
                        <span className={`text-gray-300 ${isNegative ? 'opacity-70' : ''}`}>
                          {feature}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {plan.popular ? (
                  <button
                    type="button"
                    onClick={openProForm}
                    className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 shadow-lg shadow-yellow-500/25 pointer-events-auto z-50 w-full"
                  >
                    Go Pro
                    <ArrowRight className="w-5 h-5 ml-2 inline" />
                  </button>
                ) : (
                  isLoggedIn ? (
                    <Link
                      to="/dashboard"
                      className="relative isolate inline-flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-3 rounded-full font-semibold text-lg ring-1 ring-yellow-300/40 shadow-[0_0_20px_rgba(234,179,8,0.35)] hover:from-yellow-500 hover:to-yellow-700 hover:shadow-[0_0_28px_rgba(234,179,8,0.55)] transition-all duration-200 transform hover:scale-105 z-50 overflow-visible w-full justify-center"
                    >
                      Go to Dashboard
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onClick={openCreatorForm}
                      className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 shadow-lg shadow-yellow-500/25 pointer-events-auto z-50 w-full"
                    >
                      Get Started
                    </button>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-yellow-500/10 to-transparent relative">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Transform Your Streaming Experience?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join Now and Explore New Monetization Opportunities
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                className="relative isolate inline-flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-3 rounded-full font-semibold text-lg ring-1 ring-yellow-300/40 shadow-[0_0_20px_rgba(234,179,8,0.35)] hover:from-yellow-500 hover:to-yellow-700 hover:shadow-[0_0_28px_rgba(234,179,8,0.55)] transition-all duration-200 transform hover:scale-105 z-50 overflow-visible"
              >
                Go to Dashboard
              </Link>
            ) : (
              <button
                type="button"
                onClick={openCreatorForm}
                className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 shadow-lg shadow-yellow-500/25 pointer-events-auto z-50"
              >
                Start Free Trial
              </button>
            )}
            <Link
              to="/contactus"
              className="border border-yellow-500/30 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-yellow-500/10 transition-all backdrop-blur-sm"
            >
              Contact Sales
            </Link>
          </div>
          <p className="text-sm text-gray-400 mt-4">
            No credit card required
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-800 relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <Link to="/" className="flex items-center mb-2">
                <img
                  src={logo}
                  alt="Crezar Logo"
                  className="w-12 h-12 md:w-16 md:h-16 object-contain rounded-xl"
                />
                <span className="text-2xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">
                  CREZAR
                </span>
              </Link>
              <p className="text-gray-400">
                Make every stream more engaging and unforgettable.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/dashboard" className="hover:text-yellow-400 transition-colors">Dashboard</Link></li>
                <li><a href="#pricing" className="hover:text-yellow-400 transition-colors">Pricing</a></li>
                <li><Link to="/streamers" className="hover:text-yellow-400 transition-colors">Streamers</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/contactus" className="hover:text-yellow-400 transition-colors">Contact Us</Link></li>
                {isLoggedIn && <li><Link to="/profile" className="hover:text-yellow-400 transition-colors">Profile</Link></li>}
                <li><Link to="/tnc" className="hover:text-yellow-400 transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/privacy" className="hover:text-yellow-400 transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025   Crezar. All rights reserved.</p>
          </div>
        </div>
      </footer>
      {/* Creator Pro Application Modal */}
      {showProForm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl bg-gray-900/90 border border-yellow-500/30 rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-yellow-500/20 bg-gradient-to-r from-yellow-500/10 to-transparent">
              <h4 className="text-xl font-semibold text-yellow-400">Apply for Creator Pro (Beta)</h4>
              <button onClick={closeProForm} className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-white/5" aria-label="Close">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleProFormSubmit} className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">First Name</label>
                  <div className="relative">
                    <input name="firstName" value={proFormData.firstName} onChange={handleProFormChange} required className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="John" />
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Last Name</label>
                  <div className="relative">
                    <input name="lastName" value={proFormData.lastName} onChange={handleProFormChange} required className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="Doe" />
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Contact Number</label>
                  <div className="relative">
                    <input name="phone" value={proFormData.phone} onChange={handleProFormChange} required className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="+91 98765 43210" />
                    <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Email Address</label>
                  <div className="relative">
                    <input type="email" name="email" value={proFormData.email} onChange={handleProFormChange} required className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="you@example.com" />
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">YouTube Channel Link</label>
                  <div className="relative">
                    <input name="youtube" value={proFormData.youtube} onChange={handleProFormChange} className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="https://youtube.com/@yourchannel" />
                    <Youtube className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Instagram Account</label>
                  <div className="relative">
                    <input name="instagram" value={proFormData.instagram} onChange={handleProFormChange} className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="https://instagram.com/yourhandle" />
                    <Instagram className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Location</label>
                <div className="relative">
                  <input name="location" value={proFormData.location} onChange={handleProFormChange} className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/40 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500" placeholder="City, Country" />
                  <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>
              {proFormSubmitted && (
                <p className="text-green-400 text-sm">Thank you! Our team will reach out to you within 48 Hours.</p>
              )}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={closeProForm} className="px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-white/5">Cancel</button>
                <button type="submit" disabled={proFormSubmitting} className={`px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold hover:from-yellow-500 hover:to-yellow-700 ${proFormSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  {proFormSubmitting ? 'Submitting…' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
